import { categories } from "@sobidogam/catalog";
import {
	activeCategoryLimits,
	addHouseholdItem,
	addShoppingEntry,
	calculateCategoryLimits,
	calculateLivingBudget,
	completeShopping,
	forecastRestock,
	formatKstDate,
	type HouseholdArea,
	type HouseholdConsumptionState,
	type HouseholdStockStatus,
	hasLivingBudget,
	isBudgetBucketSet,
	itemCadenceDays,
	type LivingBudgetBucket,
	mergeHouseholdItems,
	moveHouseholdItem,
	queueRestock,
	type RecurringPayment,
	removeHouseholdItem,
	renameHouseholdItem,
	resolveLivingBudgetPlan,
	setBudgetLimit,
	setCategoryLimit,
	setHouseholdItemStatus,
	undoShopping,
} from "@sobidogam/domain";
import type { Channel } from "@sobidogam/platform-ports";
import type { Receipt } from "@sobidogam/storage";
import { useEffect, useRef, useState } from "react";
import type { Draft } from "./App";
import { Icon } from "./Icons";
import { REPURCHASE_LABELS, SATISFACTION_LABELS } from "./purchase-labels";
import { RecurringPayments } from "./RecurringPayments";
import type { ShareOutcome } from "./share";

const BUDGET_LABELS: Record<LivingBudgetBucket, string> = {
	food: "먹고 마신 생활비",
	household: "집에서 쓰는 물건",
	recurring: "매달 나가는 비용",
};

const AREA_LABELS: Record<HouseholdArea, string> = {
	kitchen: "주방",
	bath: "욕실",
	laundry: "세탁",
	pet: "반려생활",
	other: "그 밖의 곳",
};

const STATUS_LABELS: Record<HouseholdStockStatus, string> = {
	in_stock: "있어요",
	low: "거의 없음",
	out: "다 썼어",
};

function formatWon(value: number) {
	return `${value.toLocaleString("ko-KR")}원`;
}

export function HouseholdPage({
	channel,
	receipts,
	state,
	onChange: applyHousehold,
	onRecordRecurring,
	onNavigate,
	onEvent,
	onShareList,
	onAddToCalendar,
}: {
	onAddToCalendar?: ((payments: RecurringPayment[]) => void) | undefined;
	/** #34: send the pending list as text (share sheet, Toss share or copy). */
	onShareList: (text: string) => Promise<ShareOutcome>;
	channel: Channel;
	receipts: Receipt[];
	state: HouseholdConsumptionState;
	onChange: (state: HouseholdConsumptionState) => Promise<boolean>;
	onRecordRecurring: (draft: Draft) => Promise<boolean>;
	onNavigate: (path: string) => void;
	onEvent: (
		name:
			| "household_item_added"
			| "household_item_status"
			| "shopping_item_checked"
			| "living_budget_updated"
			| "monthly_close_view"
			| "share_sent",
		props: Record<string, string | number | boolean>,
	) => void;
}) {
	const [view, setView] = useState("shopping");
	const [busy, setBusy] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [selectedItem, setSelectedItem] = useState<string | null>(null);
	const [budgetDraft, setBudgetDraft] = useState<
		Partial<Record<LivingBudgetBucket, string>>
	>({});
	const commit = async (next: HouseholdConsumptionState): Promise<boolean> => {
		if (busy) return false;
		setBusy(true);
		setSaveError("");
		try {
			const ok = await applyHousehold(next);
			if (!ok)
				setSaveError("저장하지 못했어요. 입력을 확인하고 다시 시도해 주세요.");
			return ok;
		} catch {
			setSaveError("저장하지 못했어요. 입력을 확인하고 다시 시도해 주세요.");
			return false;
		} finally {
			setBusy(false);
		}
	};
	const [itemName, setItemName] = useState("");
	const [itemArea, setItemArea] = useState<HouseholdArea>("kitchen");
	const [shoppingName, setShoppingName] = useState("");
	const monthStartDay = Number(
		window.localStorage.getItem("sobidogam:monthStartDay") ?? "1",
	);
	const plan = resolveLivingBudgetPlan(state, new Date(), monthStartDay);
	const budget = calculateLivingBudget(
		receipts.filter((receipt) => !receipt.deletedAt),
		plan,
	);
	const monthlyCloseRef = useRef<HTMLElement>(null);
	const reportedPeriod = useRef<string | null>(null);
	const pending = state.shopping.filter((entry) => entry.status === "pending");
	const budgetSet = hasLivingBudget(state, plan);
	const [editingItem, setEditingItem] = useState<{
		itemId: string;
		name: string;
		area: HouseholdArea;
		duplicateOf?: string | undefined;
	} | null>(null);
	const [undoState, setUndoState] = useState<{
		label: string;
		before: HouseholdConsumptionState;
	} | null>(null);
	const [listNotice, setListNotice] = useState("");
	const [limitCategory, setLimitCategory] = useState("food-cafe");
	const [limitAmount, setLimitAmount] = useState("");
	useEffect(() => {
		if (!undoState) return;
		const timer = window.setTimeout(() => setUndoState(null), 6000);
		return () => window.clearTimeout(timer);
	}, [undoState]);
	const forecasts = forecastRestock(state.items, receipts).filter(
		(forecast) => !pending.some((entry) => entry.itemId === forecast.itemId),
	);
	const categoryLimits = calculateCategoryLimits(
		receipts.filter((receipt) => !receipt.deletedAt),
		activeCategoryLimits(state, plan),
		monthStartDay,
	);
	const addForecastToList = (itemId: string, name: string) => {
		const at = new Date().toISOString();
		void commit(
			queueRestock(state, {
				itemId,
				name,
				at,
				entryId: `forecast:${itemId}:${at}`,
			}),
		);
	};
	const lastPurchase = (itemId: string) => {
		const item = state.items.find((candidate) => candidate.itemId === itemId);
		const receipt = receipts
			.filter(
				(candidate) =>
					!candidate.deletedAt &&
					(item?.sourceReceiptIds ?? []).includes(candidate.receiptId),
			)
			.sort((a, b) => b.transactedAt.localeCompare(a.transactedAt))[0];
		const key = (value: string) =>
			value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
		const line = receipt?.items?.find(
			(line) =>
				item &&
				(item.sourceReceiptLines?.[receipt.receiptId]?.includes(
					key(line.name),
				) ||
					key(line.name) === key(item.name) ||
					item.itemId === `receipt:${receipt.receiptId}:${key(line.name)}`),
		);
		const cost =
			line &&
			line.amount !== null &&
			line.qty !== null &&
			Number.isFinite(line.amount) &&
			Number.isFinite(line.qty) &&
			line.amount >= 0 &&
			line.qty > 0
				? line.amount / line.qty
				: null;
		return { cost, store: receipt?.storeDisplay };
	};
	const shoppingSuggestions = shoppingName.trim()
		? [...state.items]
				.filter(
					(item) =>
						item.name.includes(shoppingName.trim()) &&
						!pending.some((entry) => entry.name === item.name),
				)
				.sort((a, b) => b.purchaseCount - a.purchaseCount)
				.slice(0, 10)
		: [];

	useEffect(() => {
		const target = monthlyCloseRef.current;
		if (!target || reportedPeriod.current === budget.period) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries.some((entry) => entry.isIntersecting)) return;
				reportedPeriod.current = budget.period;
				onEvent("monthly_close_view", { period: budget.period });
				observer.disconnect();
			},
			{ threshold: 0.35 },
		);
		observer.observe(target);
		return () => observer.disconnect();
	}, [budget.period, onEvent]);

	const changeStatus = (itemId: string, status: HouseholdStockStatus) => {
		const now = new Date().toISOString();
		void commit(setHouseholdItemStatus(state, itemId, status, now));
		onEvent("household_item_status", { status, queued: status !== "in_stock" });
	};

	return (
		<>
			<header className="app-header" data-channel={channel}>
				{channel === "web" ? (
					<button
						className="back-link plain-link"
						type="button"
						onClick={() => onNavigate("/")}
					>
						‹ 소비도감
					</button>
				) : (
					<span aria-hidden="true" />
				)}
				<span className="app-title">우리 집 소비</span>
				<button
					className="icon-button help-button"
					type="button"
					aria-label="기록하는 길"
					title="기록하는 길"
					onClick={() => onNavigate("/app/entry")}
				>
					<Icon name="help" />
				</button>
				<button
					className="icon-button"
					type="button"
					aria-label="설정"
					onClick={() => onNavigate("/app/settings")}
				>
					⚙
				</button>
			</header>
			<main className="app-main household-page daily-household">
				<div className="daily-heading">
					<h1>
						{view === "shopping"
							? "이번 장보기"
							: view === "stock"
								? "집 안 물건"
								: view === "budget"
									? "생활비"
									: "정기 결제"}
					</h1>
					<p>
						{view === "shopping"
							? "다 쓴 물건과 이번에 살 것을 모아요"
							: "기록한 소비가 다음 선택으로 이어져요"}
					</p>
				</div>
				<fieldset className="task-tabs" aria-label="생활 기록 보기">
					{[
						["shopping", "장보기"],
						["stock", "물건"],
						["budget", "생활비"],
						["recurring", "정기 결제"],
					].map(([id, label]) => (
						<button
							type="button"
							key={id}
							aria-pressed={view === id}
							onClick={() => {
								setView(id!);
								setSaveError("");
							}}
						>
							{label}
						</button>
					))}
				</fieldset>
				{saveError && (
					<p className="form-error" role="alert">
						{saveError}
					</p>
				)}
				{view === "recurring" && (
					<RecurringPayments
						state={state}
						receipts={receipts}
						onChange={commit}
						onRecord={onRecordRecurring}
						onAddToCalendar={onAddToCalendar}
					/>
				)}
				<section
					className="household-section budget-section"
					hidden={view !== "budget"}
				>
					{!budgetSet && (
						<p className="notice">
							예산 미설정 · 한도를 정하면 남은 금액을 보여요.
						</p>
					)}
					<div className="household-section-head">
						<div>
							<p className="household-section-label">이번 생활비</p>
							<h2>세 가지만 봐요</h2>
						</div>
						<span>
							{monthStartDay === 1
								? "1일부터 이달 말일까지"
								: `${monthStartDay}일부터 다음 달 ${monthStartDay - 1}일까지`}
						</span>
					</div>
					<div className="budget-grid">
						{(Object.keys(BUDGET_LABELS) as LivingBudgetBucket[]).map(
							(bucket) => {
								const used = budget.spent[bucket];
								const limit = plan.limits[bucket];
								const bucketSet = isBudgetBucketSet(state, plan, bucket);
								const over = bucketSet && used > limit;
								return (
									<article
										className={over ? "budget-card is-over" : "budget-card"}
										key={bucket}
									>
										<span>{BUDGET_LABELS[bucket]}</span>
										<strong>{formatWon(used)}</strong>
										<label>
											월 한도
											<input
												inputMode="numeric"
												aria-label={`${BUDGET_LABELS[bucket]} 월 한도`}
												value={
													budgetDraft[bucket] ??
													(bucketSet ? String(limit) : "")
												}
												placeholder="설정 안 함"
												onChange={(event) => {
													setBudgetDraft({
														...budgetDraft,
														[bucket]: event.target.value.replace(/[^0-9]/g, ""),
													});
												}}
											/>
										</label>
										<small>
											{!bucketSet
												? "아직 한도를 정하지 않았어요"
												: over
													? `${formatWon(used - limit)} 넘었어요`
													: `${formatWon(limit - used)} 남았어요`}
										</small>
									</article>
								);
							},
						)}
					</div>
					<button
						type="button"
						className="button"
						disabled={busy || !Object.keys(budgetDraft).length}
						onClick={async () => {
							let next = state;
							for (const [key, value] of Object.entries(budgetDraft)) {
								if (!value || !Number.isSafeInteger(Number(value))) {
									setSaveError("변경한 월 한도에 0 이상의 금액을 적어 주세요.");
									return;
								}
								next = setBudgetLimit(
									next,
									plan,
									key as LivingBudgetBucket,
									Number(value),
								);
							}
							if (await commit(next)) {
								setBudgetDraft({});
								onEvent("living_budget_updated", { bucket: "all", value: 1 });
							}
						}}
					>
						예산 저장
					</button>
				</section>

				<section className="household-section" hidden={view !== "stock"}>
					<div className="household-section-head">
						<div>
							<p className="household-section-label">집 안 소모품</p>
							<h2>개수 말고 상태만</h2>
						</div>
						{state.items.length > 0 ? (
							<span>{state.items.length}개 관리 중</span>
						) : null}
					</div>
					<form
						className="household-add"
						onSubmit={async (event) => {
							event.preventDefault();
							const next = addHouseholdItem(state, {
								itemId: crypto.randomUUID(),
								name: itemName,
								area: itemArea,
								at: new Date().toISOString(),
							});
							if (next !== state) {
								if (await commit(next)) {
									onEvent("household_item_added", { area: itemArea });
									setItemName("");
								}
							}
						}}
					>
						<input
							value={itemName}
							onChange={(event) => setItemName(event.target.value)}
							placeholder="예: 세탁 세제"
							aria-label="집에서 쓰는 물건 이름"
						/>
						<select
							value={itemArea}
							onChange={(event) =>
								setItemArea(event.target.value as HouseholdArea)
							}
							aria-label="물건을 쓰는 곳"
						>
							{Object.entries(AREA_LABELS).map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</select>
						<button
							className="button button-small"
							type="submit"
							disabled={busy || !itemName.trim()}
						>
							추가
						</button>
					</form>
					{state.items.length === 0 ? (
						<div className="household-empty">
							<strong>자주 사는 것부터 하나만 적어 보세요.</strong>
							<p>
								다 쓰는 순간 장보기로 이어지고, 두 번 이상 반복되면 보통 며칠
								쓰는지도 보여 드려요.
							</p>
						</div>
					) : (
						<div className="household-items">
							{state.items.map((item) => {
								const cadence = itemCadenceDays(item);
								return (
									<article
										className={`household-item status-${item.status}`}
										key={item.itemId}
									>
										<div className="household-item-main">
											<span>{AREA_LABELS[item.area]}</span>
											<button
												type="button"
												className="item-detail-button"
												aria-expanded={selectedItem === item.itemId}
												onClick={() =>
													setSelectedItem(
														selectedItem === item.itemId ? null : item.itemId,
													)
												}
											>
												<strong>{item.name}</strong>
												<span>구매 이력 보기</span>
											</button>
											<small>
												{cadence
													? `보통 ${cadence}일 써요`
													: item.purchaseCount
														? `${item.purchaseCount}번 다시 샀어요`
														: "첫 사용 주기를 기다려요"}
											</small>
										</div>
										<fieldset className="stock-actions">
											<legend className="sr-only">{item.name} 상태</legend>
											{(
												Object.keys(STATUS_LABELS) as HouseholdStockStatus[]
											).map((status) => (
												<button
													className={item.status === status ? "active" : ""}
													type="button"
													key={status}
													aria-pressed={item.status === status}
													onClick={() => changeStatus(item.itemId, status)}
												>
													{STATUS_LABELS[status]}
												</button>
											))}
										</fieldset>
										<div className="item-secondary-actions">
											<button
												type="button"
												aria-expanded={editingItem?.itemId === item.itemId}
												onClick={() =>
													setEditingItem(
														editingItem?.itemId === item.itemId
															? null
															: {
																	itemId: item.itemId,
																	name: item.name,
																	area: item.area,
																},
													)
												}
											>
												고치기
											</button>
											<button
												type="button"
												onClick={() =>
													void commit({
														...state,
														items: state.items.map((candidate) =>
															candidate.itemId === item.itemId
																? {
																		...candidate,
																		openedAt: new Date().toISOString(),
																		finishedAt: null,
																		updatedAt: new Date().toISOString(),
																	}
																: candidate,
														),
													})
												}
											>
												오늘 열었어요
											</button>
											<button
												className={item.avoidNext ? "active" : ""}
												type="button"
												aria-pressed={item.avoidNext}
												onClick={() =>
													void commit({
														...state,
														items: state.items.map((candidate) =>
															candidate.itemId === item.itemId
																? {
																		...candidate,
																		avoidNext: !candidate.avoidNext,
																		updatedAt: new Date().toISOString(),
																	}
																: candidate,
														),
														shopping: item.avoidNext
															? state.shopping
															: state.shopping.filter(
																	(entry) =>
																		entry.itemId !== item.itemId ||
																		entry.status !== "pending",
																),
													})
												}
											>
												{item.avoidNext ? "다시 살게요" : "다시 안 사요"}
											</button>
										</div>
										{editingItem?.itemId === item.itemId ? (
											<div className="item-edit">
												<label>
													이름
													<input
														value={editingItem.name}
														maxLength={80}
														onChange={(event) =>
															setEditingItem({
																...editingItem,
																name: event.target.value,
																duplicateOf: undefined,
															})
														}
													/>
												</label>
												<label>
													쓰는 곳
													<select
														value={editingItem.area}
														onChange={(event) =>
															setEditingItem({
																...editingItem,
																area: event.target.value as HouseholdArea,
															})
														}
													>
														{Object.entries(AREA_LABELS).map(
															([value, label]) => (
																<option key={value} value={value}>
																	{label}
																</option>
															),
														)}
													</select>
												</label>
												{editingItem.duplicateOf ? (
													<p className="notice" role="alert">
														같은 이름의 물건이 이미 있어요. 합치면 구매 이력이
														하나로 모여요.
													</p>
												) : null}
												<div className="item-edit-actions">
													{editingItem.duplicateOf ? (
														<button
															type="button"
															className="button button-small"
															disabled={busy}
															onClick={async () => {
																const at = new Date().toISOString();
																if (
																	await commit(
																		mergeHouseholdItems(
																			state,
																			item.itemId,
																			editingItem.duplicateOf ?? "",
																			at,
																		),
																	)
																) {
																	setUndoState({
																		label: `${item.name}을 합쳤어요`,
																		before: state,
																	});
																	setEditingItem(null);
																}
															}}
														>
															합치기
														</button>
													) : (
														<button
															type="button"
															className="button button-small"
															disabled={busy || !editingItem.name.trim()}
															onClick={async () => {
																const at = new Date().toISOString();
																const renamed = renameHouseholdItem(
																	state,
																	item.itemId,
																	editingItem.name,
																	at,
																);
																if (!renamed.ok) {
																	if (renamed.reason === "duplicate")
																		setEditingItem({
																			...editingItem,
																			duplicateOf: renamed.duplicateItemId,
																		});
																	return;
																}
																if (
																	await commit(
																		moveHouseholdItem(
																			renamed.state,
																			item.itemId,
																			editingItem.area,
																			at,
																		),
																	)
																)
																	setEditingItem(null);
															}}
														>
															저장
														</button>
													)}
													<button
														type="button"
														className="quiet-button"
														disabled={busy}
														onClick={async () => {
															if (
																await commit(
																	removeHouseholdItem(state, item.itemId),
																)
															) {
																setUndoState({
																	label: `물건 ‘${item.name}’ 삭제했어요`,
																	before: state,
																});
																setEditingItem(null);
															}
														}}
													>
														지우기
													</button>
												</div>
											</div>
										) : null}
										{selectedItem === item.itemId && (
											<section
												className="item-history"
												aria-label={item.name + " 구매 이력"}
											>
												<h3>구매와 사용 후 생각</h3>
												{receipts
													.filter(
														(receipt) =>
															!receipt.deletedAt &&
															item.sourceReceiptIds.includes(receipt.receiptId),
													)
													.map((receipt) => (
														<article key={receipt.receiptId}>
															<strong>{receipt.storeDisplay}</strong>
															<p>
																{formatKstDate(receipt.transactedAt)} · 결제
																전체 {formatWon(receipt.totalAmount)}
															</p>
															<p>
																{receipt.afterNote ||
																	"사용 후 생각을 아직 남기지 않았어요"}
															</p>
															<p>
																{receipt.satisfaction
																	? SATISFACTION_LABELS[receipt.satisfaction]
																	: "만족도 기록 전"}
															</p>
															<p>
																{receipt.repurchaseIntent
																	? REPURCHASE_LABELS[receipt.repurchaseIntent]
																	: "재구매 판단 전"}
															</p>
															<button
																className="quiet-button"
																type="button"
																onClick={() =>
																	onNavigate(
																		"/app/records?reflect=" + receipt.receiptId,
																	)
																}
															>
																만족도·다시 살지 남기기
															</button>
														</article>
													))}
												{!item.sourceReceiptIds.length && (
													<p>결제 기록에 산 물건을 남기면 여기 연결돼요.</p>
												)}
											</section>
										)}
									</article>
								);
							})}
						</div>
					)}
				</section>

				<section
					className="household-section shopping-section"
					hidden={view !== "shopping"}
				>
					<div className="household-section-head">
						<div>
							<p className="household-section-label">우리 집 장보기</p>
							<h2>다 쓴 것만 모아서</h2>
						</div>
						<span>{pending.length}개 남음</span>
					</div>
					{forecasts.length ? (
						<div className="restock-forecast tag-band-list">
							<h3>곧 떨어질 물건</h3>
							{forecasts.map((forecast) => (
								<p className="tag-band" key={forecast.itemId}>
									<span>
										{forecast.name} ·{" "}
										{forecast.daysLeft <= 0
											? "다 썼을 때가 됐어요"
											: `${forecast.daysLeft}일 뒤쯤`}
										<small>보통 {forecast.cadenceDays}일마다 샀어요</small>
									</span>
									<button
										type="button"
										disabled={busy}
										onClick={() =>
											addForecastToList(forecast.itemId, forecast.name)
										}
									>
										담기
									</button>
								</p>
							))}
						</div>
					) : state.items.length ? (
						<p className="field-hint">
							두 번 이상 산 물건부터 떨어질 날을 알려 드려요.
						</p>
					) : null}
					<form
						className="shopping-add"
						onSubmit={async (event) => {
							event.preventDefault();
							const next = addShoppingEntry(state, {
								entryId: crypto.randomUUID(),
								name: shoppingName,
								at: new Date().toISOString(),
							});
							if (next !== state) {
								if (await commit(next)) setShoppingName("");
							}
						}}
					>
						<input
							value={shoppingName}
							onChange={(event) => setShoppingName(event.target.value)}
							placeholder="장볼 것 바로 추가"
							aria-label="장보기 항목"
						/>
						<button type="submit" disabled={busy || !shoppingName.trim()}>
							목록에 넣기
						</button>
					</form>
					{shoppingSuggestions.length ? (
						<ul className="shopping-suggestions" aria-label="자주 산 물건">
							{shoppingSuggestions.map((item) => {
								const last = lastPurchase(item.itemId);
								return (
									<li key={item.itemId}>
										<button
											type="button"
											onClick={async () => {
												const now = new Date().toISOString();
												if (
													await commit({
														...state,
														shopping: [
															...state.shopping,
															{
																entryId: crypto.randomUUID(),
																itemId: item.itemId,
																name: item.name,
																status: "pending",
																source: "manual",
																addedAt: now,
																purchasedAt: null,
																purchasedReceiptId: null,
															},
														],
													})
												)
													setShoppingName("");
											}}
										>
											<strong>{item.name}</strong>
											<small>
												{[
													last.cost !== null
														? `지난번 ${formatWon(last.cost)}`
														: null,
													last.store,
												]
													.filter(Boolean)
													.join(" · ") || `${item.purchaseCount}번 샀어요`}
											</small>
										</button>
									</li>
								);
							})}
						</ul>
					) : null}
					{pending.length === 0 ? (
						<div className="household-empty compact">
							<strong>지금 살 것은 없어요.</strong>
							<p>
								물건에서 ‘거의 없음’이나 ‘다 썼어’를 누르면 여기에 바로
								올라옵니다.
							</p>
						</div>
					) : (
						<ul className="shopping-list">
							{pending.map((entry) => (
								<li key={entry.entryId}>
									<button
										type="button"
										aria-label={`${entry.name} 구매 완료`}
										onClick={async () => {
											if (
												await commit(
													completeShopping(
														state,
														entry.entryId,
														new Date().toISOString(),
													),
												)
											)
												onEvent("shopping_item_checked", {
													source: entry.source,
												});
										}}
									>
										✓
									</button>
									<span>
										<strong>{entry.name}</strong>
										<small>
											{entry.source === "manual"
												? "직접 추가"
												: entry.source === "low"
													? "거의 없음에서 추가"
													: "다 썼어에서 추가"}
										</small>
									</span>
									<button
										className="remove-shopping"
										type="button"
										aria-label={`${entry.name} 목록에서 빼기`}
										onClick={() =>
											void commit({
												...state,
												shopping: state.shopping.filter(
													(candidate) => candidate.entryId !== entry.entryId,
												),
											})
										}
									>
										×
									</button>
								</li>
							))}
						</ul>
					)}
					<details className="completed-shopping">
						<summary>최근 구매 완료</summary>
						<ul>
							{state.shopping
								.filter((entry) => entry.status === "purchased")
								.slice(-5)
								.reverse()
								.map((entry) => (
									<li key={entry.entryId}>
										<span>{entry.name}</span>
										{entry.purchasedReceiptId ? (
											<button
												type="button"
												onClick={() => onNavigate("/app/records")}
											>
												결제 내역 보기
											</button>
										) : (
											<button
												type="button"
												disabled={busy}
												onClick={() =>
													void commit(undoShopping(state, entry.entryId))
												}
											>
												완료 취소
											</button>
										)}
									</li>
								))}
						</ul>
					</details>
					<button
						className="button button-small full-width"
						type="button"
						disabled={!pending.length}
						onClick={async () => {
							const text = [
								"장보기 부탁해요",
								...pending.map((entry) => `• ${entry.name}`),
							].join("\n");
							const outcome = await onShareList(text);
							if (outcome === "shared" || outcome === "copied")
								onEvent("share_sent", {
									kind: "shopping",
									method: outcome === "shared" ? "native" : "copy",
									channel,
								});
							setListNotice(
								outcome === "copied"
									? "목록을 복사했어요. 카톡이나 문자에 붙여 넣어 주세요."
									: outcome === "failed"
										? "보내지 못했어요. 목록을 길게 눌러 복사해 주세요."
										: "",
							);
						}}
					>
						목록 보내기
					</button>
					{!pending.length ? (
						<p className="field-hint">다 쓴 물건을 표시하면 여기 모여요.</p>
					) : null}
					{listNotice ? (
						<output className="form-note">{listNotice}</output>
					) : null}
					<button
						className="receipt-link"
						type="button"
						onClick={() => onNavigate("/app/capture")}
					>
						영수증 가져와 한 번에 채우기 →
					</button>
				</section>

				<section
					className="household-section category-limits"
					hidden={view !== "budget"}
				>
					<div className="household-section-head">
						<div>
							<p className="household-section-label">분류 한도</p>
							<h2>카페·배달처럼 하나만</h2>
						</div>
					</div>
					{categoryLimits.length ? (
						<ul className="limit-list">
							{categoryLimits.map((limit) => (
								<li
									key={limit.categoryId}
									className={
										limit.remaining < 0
											? "is-over"
											: limit.ratio >= 0.8
												? "is-near"
												: ""
									}
								>
									<span>
										{categories.find(
											(category) => category.categoryId === limit.categoryId,
										)?.nameKo ?? "기타"}
										<small>
											{formatWon(limit.spent)} / {formatWon(limit.limit)}
										</small>
									</span>
									<b>
										{limit.remaining < 0
											? `${formatWon(-limit.remaining)} 넘었어요`
											: `${formatWon(limit.remaining)} 남았어요`}
									</b>
									<button
										type="button"
										className="quiet-button"
										aria-label="한도 지우기"
										disabled={busy}
										onClick={() =>
											void commit(
												setCategoryLimit(state, plan, limit.categoryId, null),
											)
										}
									>
										지우기
									</button>
								</li>
							))}
						</ul>
					) : null}
					<form
						className="limit-add"
						onSubmit={async (event) => {
							event.preventDefault();
							const value = Number(limitAmount);
							if (!Number.isSafeInteger(value) || value <= 0) return;
							if (
								await commit(
									setCategoryLimit(state, plan, limitCategory, value),
								)
							) {
								setLimitAmount("");
								onEvent("living_budget_updated", {
									bucket: "category",
									value: 1,
								});
							}
						}}
					>
						<select
							aria-label="한도를 둘 분류"
							value={limitCategory}
							onChange={(event) => setLimitCategory(event.target.value)}
						>
							{categories
								.filter((category) => !category.sensitive)
								.map((category) => (
									<option key={category.categoryId} value={category.categoryId}>
										{category.nameKo}
									</option>
								))}
						</select>
						<input
							inputMode="numeric"
							aria-label="월 한도"
							placeholder="월 한도 (원)"
							value={limitAmount}
							onChange={(event) =>
								setLimitAmount(event.target.value.replace(/[^0-9]/g, ""))
							}
						/>
						<button
							type="submit"
							className="button button-small"
							disabled={busy || !limitAmount}
						>
							분류 한도 더하기
						</button>
					</form>
				</section>
				<section
					className="monthly-close"
					ref={monthlyCloseRef}
					hidden={view !== "budget"}
				>
					<div>
						<p className="household-section-label">월말 한 장</p>
						<h2>이번 달 집의 소비</h2>
					</div>
					<dl>
						<div>
							<dt>생활비</dt>
							<dd>{formatWon(budget.totalSpent)}</dd>
						</div>
						<div>
							<dt>다 쓴 물건</dt>
							<dd>
								{state.items.filter((item) => item.status === "out").length}개
							</dd>
						</div>
						<div>
							<dt>다시 안 살 것</dt>
							<dd>{state.items.filter((item) => item.avoidNext).length}개</dd>
						</div>
						<div>
							<dt>장보기 남음</dt>
							<dd>{pending.length}개</dd>
						</div>
					</dl>
					<p>
						수입·자산·대출은 다루지 않아요. 내가 실제로 사서 집에서 쓴
						생활소비만 한 장으로 남깁니다.
					</p>
				</section>
			</main>
			{undoState ? (
				<output className="save-toast" aria-live="polite">
					<span>
						<strong>{undoState.label}</strong>
					</span>
					<button
						type="button"
						onClick={async () => {
							if (await commit(undoState.before)) setUndoState(null);
						}}
					>
						되돌리기
					</button>
				</output>
			) : null}
		</>
	);
}
